import { useEffect, useRef, useState } from "react";

/**
 * NOTE (Kevin, 2025-12-17): This hook handles document-level file drops globally. It captures file drops
 * anywhere in the window, including areas outside the ChatFileDropZone bounds
 * (like the sidebar) and inside iframes.
 *
 * For iframe drops: Cross-origin iframes capture drag events and don't bubble
 * them to the parent. The fiber-listener inside the iframe detects drags and
 * sends IFRAME_FILE_DRAG_OVER messages to show the overlay, and IFRAME_FILE_DROP
 * messages with serialized file data when files are dropped.
 */
export function useDocumentDrop({
  onDrop,
  onRejected,
  acceptedFileFilter,
}: {
  onDrop: (files: File[]) => void;
  onRejected?: (files: File[]) => void;
  acceptedFileFilter?: (file: File) => boolean;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [isIframeDragging, setIsIframeDragging] = useState(false);

  // NOTE (Kevin, 2025-12-15): Use refs to store the latest callbacks
  // to avoid re-running the effect when the callback references change.
  const onDropRef = useRef(onDrop);
  const onRejectedRef = useRef(onRejected);
  const acceptedFileFilterRef = useRef(acceptedFileFilter);
  useEffect(() => {
    onDropRef.current = onDrop;
    onRejectedRef.current = onRejected;
    acceptedFileFilterRef.current = acceptedFileFilter;
  }, [onDrop, onRejected, acceptedFileFilter]);

  // NOTE (Kevin, 2025-12-17): Handle iframe drag/drop messages.
  // This is always active (not gated by isActive) because iframe drops should
  // work regardless of whether an interceptor is active.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "IFRAME_FILE_DRAG_OVER") {
        setIsIframeDragging(event.data.payload.isDragging);
      }

      // NOTE (Kevin, 2025-12-17): Handle file drops that occurred inside the iframe.
      // Files can't be sent directly via postMessage, so they're serialized as ArrayBuffers
      // and we reconstruct them here.
      if (event.data?.type === "IFRAME_FILE_DROP") {
        setIsIframeDragging(false);

        const serializedFiles = event.data.payload.files as {
          name: string;
          type: string;
          size: number;
          lastModified: number;
          data: ArrayBuffer;
        }[];

        const reconstructedFiles = serializedFiles.map((sf) => {
          return new File([sf.data], sf.name, {
            type: sf.type,
            lastModified: sf.lastModified,
          });
        });

        // Filter and handle files using the same logic as regular drops
        const filter = acceptedFileFilterRef.current;
        if (filter) {
          const acceptedFiles = reconstructedFiles.filter(filter);
          const rejectedFiles = reconstructedFiles.filter(
            (file) => !filter(file),
          );
          if (rejectedFiles.length > 0) {
            onRejectedRef.current?.(rejectedFiles);
          }
          onDropRef.current(acceptedFiles);
        } else {
          onDropRef.current(reconstructedFiles);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    const handleDocumentDragOver = (e: DragEvent) => {
      e.preventDefault();
      setIsDragging(true);
    };

    const handleDocumentDragLeave = (e: DragEvent) => {
      if (
        e.relatedTarget === null ||
        (e.relatedTarget as Node).nodeName === "HTML"
      ) {
        setIsDragging(false);
      }
    };

    const handleDocumentDrop = (e: DragEvent) => {
      setIsDragging(false);
      e.preventDefault();

      const allFiles = Array.from(e.dataTransfer?.files ?? []);
      if (acceptedFileFilter) {
        const acceptedFiles = allFiles.filter(acceptedFileFilter);
        const rejectedFiles = allFiles.filter(
          (file) => !acceptedFileFilter(file),
        );
        if (rejectedFiles.length > 0) {
          onRejectedRef.current?.(rejectedFiles);
        }
        onDropRef.current(acceptedFiles);
      } else {
        onDropRef.current(allFiles);
      }
    };

    document.addEventListener("dragover", handleDocumentDragOver, true);
    document.addEventListener("dragleave", handleDocumentDragLeave, true);
    document.addEventListener("drop", handleDocumentDrop);

    return () => {
      document.removeEventListener("dragover", handleDocumentDragOver, true);
      document.removeEventListener("dragleave", handleDocumentDragLeave, true);
      document.removeEventListener("drop", handleDocumentDrop);
    };
  }, [acceptedFileFilter]);

  return { isDragging: isDragging || isIframeDragging };
}
